import type { ChartTooltipProps } from './types'

import {
  TooltipColor,
  TooltipColumn,
  TooltipComparisonTable,
  TooltipContainer,
  TooltipDivider,
  TooltipFooter,
  TooltipHeader,
  TooltipHeaderPrimary,
  TooltipHeaderSecondary,
  TooltipValue,
  TooltipValueContainer,
} from './styles'

/**
 * @remarks
 * When `customHeader` is provided it supersedes the header/date/time/icon,
 * so those values are ignored in favor of the custom node.
 * When `header` is provided, it will be styled with medium font weight.
 */

export function ChartTooltip({
  className,
  header,
  customHeader,
  date,
  time,
  values,
  comparison,
  headerIcon,
  footer,
}: ChartTooltipProps) {
  const hasHeaderContent = Boolean(
    customHeader || header || date || time || headerIcon,
  )
  const hasValues = values.length > 0
  const hasColorColumn = values.some((item) => item.color || item.colorClass)

  return (
    <TooltipContainer className={className}>
      {hasHeaderContent && (
        <>
          <TooltipHeader>
            {customHeader ?? (
              <>
                {header && (
                  <TooltipHeaderPrimary>{header}</TooltipHeaderPrimary>
                )}
                {date && <TooltipHeaderPrimary>{date}</TooltipHeaderPrimary>}
                {time && (
                  <TooltipHeaderSecondary>{time}</TooltipHeaderSecondary>
                )}
                {headerIcon}
              </>
            )}
          </TooltipHeader>
          <TooltipDivider />
        </>
      )}
      {comparison && <TooltipComparisonTable comparison={comparison} />}
      {comparison && hasValues && <TooltipDivider />}
      {hasValues && (
        <TooltipValueContainer>
          {hasColorColumn ? (
            <TooltipColumn>
              {values.map((item, index) => (
                <TooltipColor
                  key={`color-${index}`}
                  color={item.color}
                  colorClass={item.colorClass}
                  dimmed={item.dimmed}
                />
              ))}
            </TooltipColumn>
          ) : null}

          <TooltipColumn className="w-full">
            {values.map((item, index) => (
              <TooltipValue
                key={`value-${index}`}
                label={item.label}
                value={item.value}
                dimmed={item.dimmed}
                icon={item.icon}
                semanticLabel={item.semanticLabel}
              />
            ))}
          </TooltipColumn>
        </TooltipValueContainer>
      )}
      {footer && (
        <>
          <TooltipDivider />
          <TooltipFooter>{footer}</TooltipFooter>
        </>
      )}
    </TooltipContainer>
  )
}
